// src/services/geolocation/geohash.js
// Geohash encoding/decoding and range generation for Firestore geo queries.
//
// Geohashes are base-32 strings where geographic proximity = string prefix similarity.
// A geohash prefix of length N covers a rectangular bounding box:
//
//   length  width × height (approx)
//   ──────  ────────────────────────
//   1       ≈ 5000km × 5000km
//   2       ≈ 1250km × 625km
//   3       ≈ 156km  × 156km
//   4       ≈ 39km   × 20km
//   5       ≈ 4.9km  × 4.9km    ← neighborhood scale (use for zone queries)
//   6       ≈ 1.2km  × 0.6km    ← block scale
//   7       ≈ 153m   × 153m     ← address scale (use for nearby reports)
//   8       ≈ 38m    × 19m      ← building scale
//
// Strategy for Eye on Storm:
//   Reports + Incidents:  store geohash at precision 7 for exact queries
//   Zone-level queries:   use precision 5 prefix for neighborhood radius
//   Parish-level queries: use precision 4 prefix
//
// Firestore geo query pattern (no native geo support):
//   1. Compute geohash for the query point at precision P
//   2. Get the 8 neighbors at precision P (covers the 3×3 cell grid around the point)
//   3. Each neighbor gives a [start, end] string range
//   4. Query: where('geohash', '>=', start).where('geohash', '<=', end)
//   5. Client-side: filter results by actual haversine distance

// ── Base-32 alphabet ──────────────────────────────────────────────
const BASE32 = '0123456789bcdefghjkmnpqrstuvwxyz';
const DECODE  = Object.fromEntries([...BASE32].map((c, i) => [c, i]));

// ── Encode ────────────────────────────────────────────────────────
// Returns a geohash string of the requested precision.
export function encode(lat, lon, precision = 7) {
  let minLat = -90,  maxLat = 90;
  let minLon = -180, maxLon = 180;
  let hash = '';
  let bits = 0, bitsTotal = 0, hashValue = 0;
  let isEven = true;

  while (hash.length < precision) {
    if (isEven) {
      const mid = (minLon + maxLon) / 2;
      if (lon >= mid) { hashValue = (hashValue << 1) | 1; minLon = mid; }
      else             { hashValue = (hashValue << 1);     maxLon = mid; }
    } else {
      const mid = (minLat + maxLat) / 2;
      if (lat >= mid) { hashValue = (hashValue << 1) | 1; minLat = mid; }
      else             { hashValue = (hashValue << 1);     maxLat = mid; }
    }
    isEven = !isEven;

    if (++bits === 5) {
      hash += BASE32[hashValue];
      bits = 0;
      hashValue = 0;
    }
  }
  return hash;
}

// ── Decode ────────────────────────────────────────────────────────
// Returns { lat, lon, latError, lonError } — the center and ±error of the cell.
export function decode(hash) {
  const { minLat, maxLat, minLon, maxLon } = decodeBbox(hash);
  return {
    lat:      (minLat + maxLat) / 2,
    lon:      (minLon + maxLon) / 2,
    latError: (maxLat - minLat) / 2,
    lonError: (maxLon - minLon) / 2,
  };
}

// ── Decode to bounding box ────────────────────────────────────────
export function decodeBbox(hash) {
  let minLat = -90,  maxLat = 90;
  let minLon = -180, maxLon = 180;
  let isEven = true;

  for (const c of hash) {
    const val = DECODE[c];
    if (val === undefined) throw new Error(`Invalid geohash character: ${c}`);
    for (let bits = 4; bits >= 0; bits--) {
      const bit = (val >> bits) & 1;
      if (isEven) {
        const mid = (minLon + maxLon) / 2;
        if (bit) minLon = mid; else maxLon = mid;
      } else {
        const mid = (minLat + maxLat) / 2;
        if (bit) minLat = mid; else maxLat = mid;
      }
      isEven = !isEven;
    }
  }
  return { minLat, maxLat, minLon, maxLon };
}

// ── Neighbor ──────────────────────────────────────────────────────
// Returns the geohash of the cell adjacent in the given direction.
// Directions: 'n' | 's' | 'e' | 'w' | 'ne' | 'nw' | 'se' | 'sw'
const NEIGHBORS = {
  n: ['p0r21436x8zb9dcf5h7kjnmqesgutwvy', 'bc01fg45telehmjnpqrutuvwxy'],
  s: ['14365h7k9dcfesgujnmqp0r2twvyx8zb', '238967debc01telehjklnpqrstuvwxy'],  // corrected
  e: ['bc01fg45telehmjnpqrutuvwxy', 'p0r21436x8zb9dcf5h7kjnmqesgutwvy'],
  w: ['238967debc01telehjklnpqrstuvwxy', '14365h7k9dcfesgujnmqp0r2twvyx8zb'],
};
const BORDERS = {
  n: ['prxz', 'bcfguvyz'],
  s: ['028b', '0145hjnp'],
  e: ['bcfguvyz', 'prxz'],
  w: ['0145hjnp', '028b'],
};

export function neighbor(hash, direction) {
  const lastChar = hash[hash.length - 1];
  const parent   = hash.slice(0, -1);
  const typ      = hash.length % 2;

  // Handle boundary cases by recursing on parent
  if (BORDERS[direction][typ].includes(lastChar) && parent.length > 0) {
    return neighbor(parent, direction) + BASE32[NEIGHBORS[direction][typ].indexOf(lastChar)];
  }
  return parent + BASE32[NEIGHBORS[direction][typ].indexOf(lastChar)];
}

// ── All 8 neighbors ───────────────────────────────────────────────
export function neighbors(hash) {
  const n  = neighbor(hash, 'n');
  const s  = neighbor(hash, 's');
  const e  = neighbor(hash, 'e');
  const w  = neighbor(hash, 'w');
  return {
    n, s, e, w,
    ne: neighbor(n, 'e'),
    nw: neighbor(n, 'w'),
    se: neighbor(s, 'e'),
    sw: neighbor(s, 'w'),
  };
}

// ── Radius → precision ────────────────────────────────────────────
// Returns the geohash precision that best covers a given radius.
// Chosen so the geohash cell is smaller than the radius (false positives
// are filtered client-side by haversine; false negatives are eliminated
// by querying all 9 cells in the 3×3 neighborhood).
const PRECISION_MAP = [
  { maxKm: 0.075, precision: 8 },
  { maxKm: 0.6,   precision: 7 },
  { maxKm: 2.4,   precision: 6 },
  { maxKm: 19.5,  precision: 5 },
  { maxKm: 78,    precision: 4 },
  { maxKm: 630,   precision: 3 },
];

export function radiusToPrecision(radiusKm) {
  for (const { maxKm, precision } of PRECISION_MAP) {
    if (radiusKm <= maxKm) return precision;
  }
  return 2;
}

// ── Query ranges for Firestore ────────────────────────────────────
// Returns an array of { start, end } string ranges covering the given
// radius around a lat/lon. Each range maps to one Firestore IN query.
//
// Usage:
//   const ranges = geohashQueryRanges(lat, lon, radiusKm);
//   for (const { start, end } of ranges) {
//     const q = query(collection, where('geohash', '>=', start), where('geohash', '<=', end));
//     // fetch and filter by haversine
//   }
//
// Firestore limits: max 10 disjunctive conditions per query.
// We return 9 cells (center + 8 neighbors) — within the limit.
export function geohashQueryRanges(lat, lon, radiusKm) {
  const precision = radiusToPrecision(radiusKm);
  const center    = encode(lat, lon, precision);
  const allCells  = [center, ...Object.values(neighbors(center))];

  // Merge adjacent ranges to minimize query count
  const sorted  = [...new Set(allCells)].sort();
  const ranges  = [];
  let current   = null;

  for (const cell of sorted) {
    if (!current) {
      current = { start: cell, end: cell + '\uf8ff' };
    } else if (cell.slice(0, -1) === current.end.slice(0, -1)) {
      // Adjacent — extend the range
      current.end = cell + '\uf8ff';
    } else {
      ranges.push(current);
      current = { start: cell, end: cell + '\uf8ff' };
    }
  }
  if (current) ranges.push(current);

  return ranges;
}

// ── Convenience: geohash for a point at specific precision ────────
export const geohashForReport   = (lat, lon) => encode(lat, lon, 7);
export const geohashForZone     = (lat, lon) => encode(lat, lon, 5);
export const geohashForParish   = (lat, lon) => encode(lat, lon, 4);

// ── Bounding box from center + radius ─────────────────────────────
// Used for quick bbox pre-filter before haversine.
export function boundingBox(lat, lon, radiusKm) {
  const latDelta = radiusKm / 111.32;
  const lonDelta = radiusKm / (111.32 * Math.cos(lat * Math.PI / 180));
  return {
    minLat: lat - latDelta,
    maxLat: lat + latDelta,
    minLon: lon - lonDelta,
    maxLon: lon + lonDelta,
  };
}
